import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { type Bonus, BonusStage, type Freespin } from '@/api/common';
import { useGetCommonStore } from '@/store';
import type { IHeadlinePromoPageConfig } from '../../../helpers';
import {
  applyLevelSegment,
  getCurrentLevelIndex,
  getMissionLevels,
  getTotalBonusLabel,
} from '../helpers';

const getRewardGroupKeys = (bonuses: Bonus[], freespins: Freespin[]) => {
  const canceled = new Set<string | undefined>();
  const issued = new Set<string | undefined>();

  for (const { stage, groupKey } of [...bonuses, ...freespins]) {
    if (stage === BonusStage.Canceled) canceled.add(groupKey);
    else if (stage === BonusStage.Issued) issued.add(groupKey);
  }

  return { canceled, issued };
};

type IMissionLevelsConfig = Pick<
  IHeadlinePromoPageConfig,
  | 'levelsConfig'
  | 'groupPrefix'
  | 'useFullNameAsGroupId'
  | 'currencyCode'
  | 'hideCardProgressCurrency'
>;

export const useMissionLevels = ({
  levelsConfig,
  groupPrefix,
  useFullNameAsGroupId = false,
  currencyCode,
  hideCardProgressCurrency = false,
}: IMissionLevelsConfig) => {
  const { i18n } = useTranslation();

  const useCommonStore = useGetCommonStore();

  const { user, bonuses, freespins, bonusesPending, freespinsPending } =
    useCommonStore(
      useShallow((state) => ({
        user: state.user,
        bonuses: state.bonuses,
        freespins: state.freespins,
        bonusesPending: state.bonusesPending,
        freespinsPending: state.freespinsPending,
      }))
    );
  const isBonusDataPending = bonusesPending || freespinsPending;

  // Logout doesn't reset bonuses/freespins.
  const rewardGroupKeys = useMemo(
    () => (user ? getRewardGroupKeys(bonuses, freespins) : undefined),
    [user, bonuses, freespins]
  );

  const segmentLevelsConfig = useMemo(
    () =>
      user
        ? applyLevelSegment(
            levelsConfig,
            (user.statuses || []).map(({ id }) => id),
            new Set(
              [...bonuses, ...freespins].flatMap(({ groupKey }) =>
                groupKey ? [groupKey] : []
              )
            )
          )
        : levelsConfig,
    [levelsConfig, user, bonuses, freespins]
  );

  const levels = useMemo(
    () =>
      getMissionLevels(
        groupPrefix,
        useFullNameAsGroupId,
        segmentLevelsConfig,
        user,
        rewardGroupKeys,
        isBonusDataPending,
        {
          currencyCode: currencyCode ?? user?.currency,
          locale: i18n.language,
          hideCardProgressCurrency,
        }
      ),
    [
      groupPrefix,
      useFullNameAsGroupId,
      segmentLevelsConfig,
      user,
      rewardGroupKeys,
      isBonusDataPending,
      currencyCode,
      i18n.language,
      hideCardProgressCurrency,
    ]
  );
  const currentLevelIndex = useMemo(
    () => getCurrentLevelIndex(levels),
    [levels]
  );

  const totalBonusLabel = useMemo(
    () =>
      getTotalBonusLabel(segmentLevelsConfig, currencyCode ?? user?.currency),
    [segmentLevelsConfig, currencyCode, user?.currency]
  );

  return { levels, currentLevelIndex, totalBonusLabel };
};
